import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Search, 
  Filter, 
  MapPin, 
  Calendar, 
  ArrowRight, 
  X, 
  UserCircle, 
  Award, 
  Languages, 
  Clock,
  Stethoscope,
  Baby,
  Eye,
  Ear,
  Activity,
  Phone
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icon mapping for doctor specializations
const getSpecializationIcon = (iconType, specialty, size = "w-12 h-12") => {
  const iconClass = `${size} text-primary`;
  
  switch (iconType) {
    case 'dental':
      return (
        <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2C7.5 2 6 5 6 8c0 2 .5 3 1 4.5.5 1.5 1 3 1 5.5 0 2 1 4 2.5 4s2-1.5 2.5-3.5c.5 2 1 3.5 2.5 3.5s2.5-2 2.5-4c0-2.5.5-4 1-5.5S18 10 18 8c0-3-1.5-6-6-6z"/>
        </svg>
      );
    case 'eye':
      return <Eye className={iconClass} />;
    case 'maternal':
      return (
        <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="8" r="5"/>
          <path d="M12 13c-4 0-7 2-7 5v2h14v-2c0-3-3-5-7-5z"/>
          <circle cx="12" cy="8" r="2"/>
        </svg>
      );
    case 'pediatric':
      return <Baby className={iconClass} />;
    case 'physiotherapy':
      return <Activity className={iconClass} />;
    case 'ent':
      return <Ear className={iconClass} />;
    case 'stethoscope':
    default:
      return <Stethoscope className={iconClass} />;
  }
};

// Department options
const departmentOptions = [
  { id: 'dept-1', name: 'General Medicine' },
  { id: 'dept-2', name: 'Pediatrics' },
  { id: 'dept-3', name: 'Maternity & Obstetrics' },
  { id: 'dept-4', name: 'Surgery' },
  { id: 'dept-5', name: 'Dental Services' },
  { id: 'dept-6', name: 'Eye Care' },
  { id: 'dept-7', name: 'Rehabilitation Services' }
];

// Specialty options
const specialtyOptions = [
  'Clinical Officer',
  'Medical Officer',
  'Dental Therapy',
  'Optometry',
  'Obstetrics & Gynecology',
  'Paediatrics & Child Health',
  'Physiotherapy',
  'ENT Surgery'
];

export default function FindDoctor() {
  const [doctors, setDoctors] = useState([]);
  const [departments, setDepartments] = useState(departmentOptions);
  const [loading, setLoading] = useState(true);
  const [searchName, setSearchName] = useState('');
  const [selectedDept, setSelectedDept] = useState('all');
  const [selectedSpecialty, setSelectedSpecialty] = useState('all');
  const [filteredDoctors, setFilteredDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [docRes, deptRes] = await Promise.all([
          axios.get(`${API}/doctors`),
          axios.get(`${API}/departments`)
        ]);
        
        if (docRes.data && docRes.data.length > 0) {
          setDoctors(docRes.data);
          setFilteredDoctors(docRes.data);
        }
        
        if (deptRes.data && deptRes.data.length > 0) {
          setDepartments(deptRes.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  useEffect(() => {
    let filtered = doctors;
    
    // Filter by name
    if (searchName) {
      filtered = filtered.filter(doc => 
        doc.name.toLowerCase().includes(searchName.toLowerCase())
      );
    }
    
    // Filter by department
    if (selectedDept && selectedDept !== 'all') {
      filtered = filtered.filter(doc => 
        doc.department_id === selectedDept || 
        doc.department_name?.toLowerCase().includes(selectedDept.toLowerCase())
      );
    }
    
    // Filter by specialty
    if (selectedSpecialty && selectedSpecialty !== 'all') {
      filtered = filtered.filter(doc => 
        doc.specialty?.toLowerCase().includes(selectedSpecialty.toLowerCase())
      );
    }
    
    setFilteredDoctors(filtered);
  }, [searchName, selectedDept, selectedSpecialty, doctors]);

  const clearFilters = () => {
    setSearchName('');
    setSelectedDept('all');
    setSelectedSpecialty('all');
  };

  const hasActiveFilters = searchName || selectedDept !== 'all' || selectedSpecialty !== 'all';

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary via-primary to-primary-700 py-20 md:py-28" data-testid="find-doctor-hero">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-primary-100 font-semibold mb-3 tracking-wide uppercase text-sm">Doctor Directory</p>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Find Your Doctor
            </h1>
            <p className="text-white/80 text-lg leading-relaxed mb-8">
              Search our team of qualified healthcare professionals by name, specialty, or department.
            </p>
          </div>
        </div>
      </section>

      {/* Search & Filter Section */}
      <section className="py-8 bg-slate-50 border-b sticky top-0 z-20" data-testid="find-doctor-filters">
        <div className="container-custom">
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="grid md:grid-cols-4 gap-4">
              {/* Name Search */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-2">Search by Name</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="Enter doctor's name..."
                    value={searchName}
                    onChange={(e) => setSearchName(e.target.value)}
                    className="pl-10 h-12 rounded-lg"
                    data-testid="find-doctor-name-search"
                  />
                </div>
              </div>

              {/* Specialty Filter */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Specialty</label>
                <Select value={selectedSpecialty} onValueChange={setSelectedSpecialty}>
                  <SelectTrigger className="h-12 rounded-lg" data-testid="find-doctor-specialty-filter">
                    <SelectValue placeholder="All Specialties" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Specialties</SelectItem>
                    {specialtyOptions.map((specialty) => (
                      <SelectItem key={specialty} value={specialty}>{specialty}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Department Filter */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Department</label>
                <Select value={selectedDept} onValueChange={setSelectedDept}>
                  <SelectTrigger className="h-12 rounded-lg" data-testid="find-doctor-dept-filter">
                    <SelectValue placeholder="All Departments" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    {departments.map((dept) => (
                      <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Active Filters & Clear */}
            {hasActiveFilters && (
              <div className="mt-4 pt-4 border-t flex items-center justify-between">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-slate-600">Active filters:</span>
                  {searchName && (
                    <span className="inline-flex items-center gap-1 bg-primary/10 text-primary px-3 py-1 rounded-full text-sm">
                      Name: {searchName}
                      <X className="w-3 h-3 cursor-pointer" onClick={() => setSearchName('')} />
                    </span>
                  )}
                  {selectedSpecialty !== 'all' && (
                    <span className="inline-flex items-center gap-1 bg-primary/10 text-primary px-3 py-1 rounded-full text-sm">
                      Specialty: {selectedSpecialty}
                      <X className="w-3 h-3 cursor-pointer" onClick={() => setSelectedSpecialty('all')} />
                    </span>
                  )}
                  {selectedDept !== 'all' && (
                    <span className="inline-flex items-center gap-1 bg-primary/10 text-primary px-3 py-1 rounded-full text-sm">
                      Department
                      <X className="w-3 h-3 cursor-pointer" onClick={() => setSelectedDept('all')} />
                    </span>
                  )}
                </div>
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-primary hover:text-primary-700">
                  Clear All
                </Button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-12 md:py-16" data-testid="find-doctor-results">
        <div className="container-custom">
          <div className="mb-8 flex items-center justify-between">
            <p className="text-slate-600">
              Showing <span className="font-semibold text-primary">{filteredDoctors.length}</span> doctor{filteredDoctors.length !== 1 ? 's' : ''}
            </p>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 gap-6">
              {Array(4).fill(0).map((_, i) => (
                <div key={i} className="bg-white rounded-2xl shadow-sm p-6 animate-pulse flex gap-6">
                  <div className="w-20 h-20 bg-slate-200 rounded-full" />
                  <div className="flex-1">
                    <div className="h-4 bg-slate-200 rounded w-1/3 mb-2" />
                    <div className="h-6 bg-slate-200 rounded w-3/4 mb-2" />
                    <div className="h-4 bg-slate-200 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredDoctors.length === 0 ? (
            <div className="text-center py-16 bg-slate-50 rounded-2xl">
              <Search className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="font-heading text-xl font-semibold text-slate-800 mb-2">No Doctors Found</h3>
              <p className="text-slate-600 mb-6">No doctors match your current search criteria.</p>
              <Button onClick={clearFilters} className="bg-primary hover:bg-primary-700">
                Clear All Filters
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {filteredDoctors.map((doctor, index) => (
                <Card 
                  key={doctor.id} 
                  className="bg-white shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden cursor-pointer group border border-slate-100 hover:border-primary/20"
                  onClick={() => setSelectedDoctor(doctor)}
                  data-testid={`find-doctor-card-${index}`}
                >
                  <CardContent className="p-6 flex gap-6">
                    {/* Icon or Photo */}
                    <div className="flex-shrink-0">
                      {doctor.image ? (
                        <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-primary/10">
                          <img
                            src={doctor.image}
                            alt={doctor.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center group-hover:from-primary/20 group-hover:to-primary/10 transition-colors">
                          {getSpecializationIcon(doctor.icon_type, doctor.specialty)}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex-1 flex flex-col">
                      <div className="flex-1">
                        {/* Qualification Badge */}
                        <span className="inline-block px-2 py-0.5 bg-primary/10 text-primary text-xs font-semibold rounded mb-2">
                          {doctor.specialty_code || doctor.title}
                        </span>
                        
                        {/* Doctor Name */}
                        <h3 className="font-heading font-semibold text-slate-800 text-lg mb-1 group-hover:text-primary transition-colors">
                          {doctor.name}
                        </h3>
                        
                        {/* Specialty & Department */}
                        <p className="text-primary font-medium text-sm mb-1">{doctor.specialty}</p>
                        <p className="text-slate-500 text-sm">{doctor.department_name}</p>
                      </div>
                      
                      {/* View Profile */}
                      <div className="mt-4 pt-3 border-t border-slate-100">
                        <span className="text-primary text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                          View Profile <ArrowRight className="w-4 h-4" />
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-primary to-primary-700" data-testid="find-doctor-cta">
        <div className="container-custom text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
            Found Your Doctor?
          </h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto">
            Book an appointment now and let our expert medical team take care of your health needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/book-appointment">
              <Button size="lg" className="bg-white text-primary hover:bg-slate-100 rounded-lg px-8">
                <Calendar className="w-5 h-5 mr-2" />
                Book Appointment
              </Button>
            </Link>
            <a href="tel:+255659984488">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 rounded-lg px-8">
                <Phone className="w-5 h-5 mr-2" />
                Call Us
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Doctor Profile Modal */}
      <Dialog open={!!selectedDoctor} onOpenChange={() => setSelectedDoctor(null)}>
        <DialogContent className="max-w-lg">
          {selectedDoctor && (
            <>
              <DialogHeader>
                <DialogTitle className="sr-only">Doctor Profile</DialogTitle>
              </DialogHeader>
              <div className="text-center">
                {/* Icon or Photo */}
                <div className="mb-4">
                  {selectedDoctor.image ? (
                    <div className="w-24 h-24 mx-auto rounded-full overflow-hidden border-4 border-primary/20">
                      <img
                        src={selectedDoctor.image}
                        alt={selectedDoctor.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                      {getSpecializationIcon(selectedDoctor.icon_type, selectedDoctor.specialty, "w-14 h-14")}
                    </div>
                  )}
                </div>
                
                {/* Qualification Badge */}
                <div className="mb-3">
                  <span className="inline-block px-4 py-1.5 bg-primary/10 text-primary text-sm font-semibold rounded-full">
                    {selectedDoctor.qualifications?.[0] || selectedDoctor.specialty_code || selectedDoctor.title}
                  </span>
                </div>
                
                {/* Doctor Name */}
                <h2 className="font-heading font-bold text-slate-800 text-2xl mb-2">
                  {selectedDoctor.name}
                </h2>
                
                {/* Specialty */}
                <p className="text-primary font-medium mb-4">
                  {selectedDoctor.specialty}
                </p>
                
                {/* Department */}
                <p className="text-slate-500 text-sm mb-6">
                  {selectedDoctor.department_name}
                </p>
                
                {/* Bio */}
                {selectedDoctor.bio && (
                  <p className="text-slate-600 text-sm mb-6 text-justify leading-relaxed">
                    {selectedDoctor.bio}
                  </p>
                )}
                
                {/* Languages */}
                {selectedDoctor.languages && selectedDoctor.languages.length > 0 && (
                  <div className="mb-6">
                    <p className="text-xs text-slate-400 uppercase tracking-wide mb-2">Languages</p>
                    <p className="text-slate-600 text-sm">{selectedDoctor.languages.join(', ')}</p>
                  </div>
                )}
                
                {/* Book Appointment Button */}
                <Link to="/book-appointment" onClick={() => setSelectedDoctor(null)}>
                  <Button className="w-full bg-primary hover:bg-primary-700 rounded-lg">
                    <Calendar className="w-4 h-4 mr-2" />
                    Book Appointment
                  </Button>
                </Link>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
